import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { toast } from "react-toastify";
import { Icon, useMediaQuery } from "@mui/material";

// Custom components
import { PageTitle } from "../../../components/PageTitle";
import { BackdropLoading } from "../../../components/BackdropLoading";
import { TabPanel } from "../../../components/atoms/tabPanel";
import { WorkPermitDetailTab } from "../../WorkPermitDetailTab";
import { TimeLine } from "../../../components/TimeLine";
import WorkPermitDetailData from "../../../components/atoms/workPermitDetailData";
import { WorkflowDataRelation } from "../../WorkflowDataRelation";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Styles
import { Container } from "../../styles";
import {
  SectionPageTitle,
  CustomCard,
  CustomTabs,
  ItemTab,
  ContainerContent,
  ContainerRight,
  ContainerLeft,
  CustomBadge,
  DetailInfo,
} from "./styles";

// API
import Router from "../../../router";

// Context
import { ColorsContext } from "../../../Context/ColorsContext";
import { useOffline } from "../../../Context/OfflineContext";
import { useQrScanner } from "../../../Hooks/useQrScanner";
import { QrScannerModal } from "../../../components/molecules/modals/QrScannerModal";


export const WorkflowDetail = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { getOfflineForm, getOfflineSidebar, getEditedOfflineFormData } = useOffline();
  const {
    openQrModal,
    startScanning,
    handleOpenQrScan,
    handleCloseQrModal,
    handleStartScanning,
    handleQrScan,
    handleQrError
  } = useQrScanner();
  const { colors } = React.useContext(ColorsContext);
  const isMediumOrSmaller = useMediaQuery((theme) =>
    theme.breakpoints.down("md")
  );

  const [data, setData] = useState({});
  const [workflow, setWorkflow] = useState({});
  const [formData, setFormData] = useState({});
  const [workflowDetail, setWorkflowDetail] = useState({});
  const [timeline, setTimeline] = useState([]);
  const [instanceTime, setInstanceTime] = useState(null);
  const [formVersion, setFormVersion] = useState("form_builder");
  const [detailForm, setDetailForm] = useState([]);
  const [detailPendingForm, setDetailPendingForm] = useState([]);
  const [flowRelations, setFlowRelations] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState("");
  const [idDisable, setIdDisable] = useState({});
  const currentFormRef = useRef(undefined);

  // Loading and actions messages
  const [loading, setLoading] = useState(true);
  const [formsTitles, setFormsTitles] = useState([]);
  const [pendingFormsTitles, setPendingFormsTitles] = useState([]);

  const [tab, setTab] = useState(0);
  const formRef = useRef(null);

  useEffect(() => {
    getWorkflowData();
    const offlineData = getOfflineForm(params?.id);

    if (offlineData) {
      setLoading(true);

      setData(offlineData.data);
      setWorkflow(offlineData.workflow);
      setFormData(offlineData.formData);

      const workflowData = offlineData?.workflow;

      setTimeline(workflowData.timeline || []);
      setInstanceTime(workflowData.instanceTime);
      setFormVersion(offlineData.formData?.form?.form?.renderer);

      // Setear información de formularios extra
      const extraForms = getExtraFormsInformation(workflowData, offlineData);
      // Formularios diligenciados
      setFormsTitles(workflowData.extraFormsTitles || []);
      setDetailForm(extraForms);

      // Setear información de formularios pendientes
      const extraPendingForms = getExtraPendingFormsInformation(workflowData, offlineData);
      // Formularios pendientes
      setPendingFormsTitles(workflowData.extraPendingFormsTitles || []);
      setDetailPendingForm(extraPendingForms);

      setFlowRelations(workflowData.relations || data.relations || []);

      setTitle(`${t("workflow.detail.detail")} LOTO`);
    } else {
      toast.error("No se encontró información relacionada a este workflow.");
      setTimeout(() => {
        navigate(Router.appOfflineWorkflow.replace(":id", params?.workflowId || 1075));
      })
    }
    setLoading(false);
  }, []);

  const getWorkflowData = () => {
    const sidebar = getOfflineSidebar();
    if (sidebar?.length > 0 && params?.workflowId) {
      const currentWorkflow = sidebar.find((el) => el.id.toString() === params.workflowId.toString());
      setWorkflowDetail(currentWorkflow || {});
    }
    return sidebar || {};
  }

  const getExtraFormsInformation = (workflowData, offlineData) => {
    const extraForms = workflowData?.extraForms?.map((formId) => {
      let formDataObj = {};
      if (offlineData?.formData?.form?._id === formId) {
        formDataObj = offlineData.formData;
      }
      else {
        formDataObj = offlineData?.extraForms?.find((f) => f.form?._id === formId);
      }

      if (!formDataObj) return {}
      return {
        formId,
        form: formDataObj.form || { structure: [] },
        data: formDataObj.data || {},
        characteristics: formDataObj.characteristics || {},
        parentPreviousData: formDataObj.parentPreviousData,
      };
    });

    return extraForms;
  }

  const getExtraPendingFormsInformation = (workflowData, offlineData) => {
    const extraPendingForms = workflowData?.extraPendingForms?.map((formId, index) => {
      let formDataObj = {};
      if (offlineData?.formData?.form?._id === formId) {
        formDataObj = offlineData.formData;
      }
      else {
        formDataObj = offlineData?.extraForms?.find((f) => f.form?._id === formId);
      }

      if (!formDataObj) return {}
      return {
        formId,
        notification: workflowData.extraPendingNotificationIds?.[index],
        form: formDataObj.form || { structure: [] },
        data: formDataObj.data || {},
        characteristics: formDataObj.characteristics || {},
        parentPreviousData: formDataObj.parentPreviousData,
      };
    });

    return extraPendingForms;
  }

  const handleChange = (panel, detail, index, areaData) => async (event, isExpanded) => {
    if (event?.target?.id?.includes('process-button')) return

    if (isExpanded) {
      setDetailFormData(detail, index, areaData);
    }

    if (event.target.id?.includes('pdf-button')) {
      setExpanded(panel);
    }
    else {
      setExpanded(isExpanded ? panel : false);
    }
};

  function setDetailFormData(detail) {
    if (localStorage.getItem("parentPreviousData") !== null) {
      localStorage.removeItem("parentPreviousData");
    }
    if (detail.parentPreviousData) {
      localStorage.setItem("parentPreviousData", JSON.stringify(detail.parentPreviousData));
    }
  }

  function handleChangeTab(_, value) {
    setTab(value);
  };

  const titleTab = (forms, title) => {
    const count = Array.isArray(forms) ? forms.length : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {count > 0 && <CustomBadge badgeContent={count} />}
        <span style={{ marginLeft: count > 0 ? '16px' : '0' }}>{title}</span>
      </div>
    )
  }

  async function handleProcessForm(row) {
    // Abrir modal con escáner QR via hook
    handleOpenQrScan(row, workflow);
  }

  const handleScanSuccess = (data) => {
    const { row } = data;
    if (row?.id && row?.workflowId) {
      const route = Router.appOfflineDynamicForm.replace(":id", row.id);

      navigate(route, {state: { row: row, workflow: workflow }});
      handleCloseQrModal();
    }
  };

  const validateQrLotoList = async (qrResult) => {
    try {
      setLoading(true);

      const previousFormData = formData?.parentPreviousData?.["R4ZIDQWP"];
      
      if (!previousFormData) {
        toast.error("No se encontraron datos previos en el formulario offline para validación.");
        setLoading(false);
        return false;
      }

      const container2 = previousFormData?.container?.container2;
      const equipments = container2?.equipoSistemaAbscisaObjetoDelTrabajo || [];

      const equipmentFound = equipments.some(eq => eq.id?.toString() === qrResult.equipmentCode?.toString());

      if (!equipmentFound) {
        toast.error("El equipo no coincide con el LOTO seleccionado.");
        setLoading(false);
        return false;
      }

      setLoading(false);
      return true;

    } catch (error) {
      toast.error("Ha ocurrido un error validando el código QR. Por favor, comuníquese con soporte.");
      setLoading(false);
      return false;
    }
  };

  return (
    <Container
      open={props.open}
      customWidth={props.customExternalWidth}
      mb={10}
    >
      <SectionPageTitle>
        <PageTitle title="" isbutton={true}
          navigate={Router.appOfflineWorkflow.replace(':id', params?.workflowId || 1075)}
          icon={<ArrowBackIcon />}
        />
        <PageTitle title={title}
          icon={<Icon baseClassName="material-icons-outlined">{workflowDetail?.icon || ''}</Icon>}
        />
      </SectionPageTitle>

      <ContainerContent>
        <ContainerLeft>
          <DetailInfo>
            <WorkPermitDetailData
              data={data}
              workflow={workflow}
              idDisable={idDisable}
              handleOpenStatus={handleProcessForm}
              canDownloadRevalidations={{ current: false }}
              isLotoWorkflow={true}
              hasEditedData={!!getEditedOfflineFormData(data?.id)}
            />
          </DetailInfo>

          <CustomCard id="TabsForms">
            <CustomTabs
              value={tab}
              onChange={handleChangeTab}
              textColor="secondary"
              indicatorColor="secondary"
              variant="scrollable"
              TabIndicatorProps={{
                style: {
                  backgroundColor: colors.buttonActionColor,
                  color: colors.buttonActionColor,
                },
              }}
            >
              <ItemTab
                value={0}
                label={titleTab(detailForm, t("workflow.detail.formsActive"))}
              />
              <ItemTab
                value={1}
                label={titleTab(
                  detailPendingForm,
                  t("workflow.detail.formsPending")
                )}
              />
              {flowRelations?.length > 0 && (
                <ItemTab
                  value={2}
                  label={titleTab(
                    flowRelations,
                    t("workflow.detail.flowRelations")
                  )}
                />
              )}
            </CustomTabs>
            <TabPanel value={tab} index={0} style={{ marginTop: 0 }}>
              {!loading && (
                <WorkPermitDetailTab
                  areaData={0}
                  detailForm={detailForm}
                  formsTitles={formsTitles}
                  loadingForm={false}
                  expanded={expanded}
                  data={data}
                  formVersion={formVersion}
                  formRef={formRef}
                  showPreActiveButton={false}
                  showActiveButton={false}
                  showResumeButton={false}
                  showPauseButton={false}
                  showFormButtons={false}
                  showDeleteButton={false}
                  isExternal={true}
                  handleChange={handleChange}
                  downloadPdf={null}
                  processAction={handleProcessForm}
                  currentFormRef={currentFormRef}
                />
              )}
            </TabPanel>
            <TabPanel value={tab} index={1} style={{ marginTop: 0 }}>
              {!loading && (
                <WorkPermitDetailTab
                  areaData={1}
                  detailForm={detailPendingForm}
                  formsTitles={pendingFormsTitles}
                  loadingForm={false}
                  expanded={expanded}
                  data={data}
                  formVersion={formVersion}
                  formRef={formRef}
                  isExternal={true}
                  handleChange={handleChange}
                  downloadPdf={null}
                  processAction={handleProcessForm}
                  currentFormRef={currentFormRef}
                />
              )}
            </TabPanel>
            {flowRelations?.length > 0 && (
              <TabPanel value={tab} index={2}>
                <WorkflowDataRelation data={flowRelations} />
              </TabPanel>
            )}
          </CustomCard>
        </ContainerLeft>

        <ContainerRight>
          <DetailInfo id="TimeLine">
            {!loading && (
              <TimeLine
                data={timeline}
                dataTime={instanceTime}
                plotPlans={null}
              />
            )}
          </DetailInfo>
        </ContainerRight>
      </ContainerContent>

      <BackdropLoading open={loading} />

      <QrScannerModal
        open={openQrModal}
        onClose={handleCloseQrModal}
        rowName={data?.code ?? ""}
        startScanning={startScanning}
        onStartScanning={handleStartScanning}
        onScan={(result) => handleQrScan(result, handleScanSuccess, validateQrLotoList)}
        onError={handleQrError}
      />
    </Container>
  );
};
